<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import {
		buildSerialPeripheralLabels,
		getSerialAddonStatus,
	} from 'src/helpers/addons/addon_serial_helpers';
	import { isPill, load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { hasSwitchAddon } from 'src/helpers/addons/addon_helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { proRS485AddonSupport } from 'src/helpers/addons/addon_support_helpers';
	import {
		deletePeripheralDevice,
		getPeripheralDevice,
	} from 'src/helpers/addons/addon_device_helpers';
	import { replace } from 'svelte-spa-router';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import {
		ActionsContainer,
		AdvancedCard,
		Alert,
		Button,
		Container,
		IconButton,
		LabelList,
		Modal,
		Text,
		CardStatusBar,
	} from '@Shelly/shelly-design-system';
	//Components
	import AddPeripheralModal from './AddPeripheralModal.svelte';
	import PeripheralSettings from './PeripheralSettings.svelte';
	import DeviceInList from 'src/components/Lists/Devices/DeviceInList.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Peripheral } from 'src/types/Peripherals';

	$: statusStore = getDeviceStatus(device.id);
	export let device: Device;

	const dispatcher = createEventDispatcher();
	const removePeripheral = async () => {
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(
			device,
			`${hasSwitchAddon(device) ? 'prooutputaddon' : 'sensoraddon'}.removeperipheral`,
			{ component: selectedPeripheral.sensor.component },
		);
		if (res?.isok) {
			res.restart_required = true;
			const peripheralDevice = getPeripheralDevice(real_id(device.id), selectedPeripheral);
			if (peripheralDevice) {
				await deletePeripheralDevice(peripheralDevice);
			}
		}
		removePeripheralModal = false;
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	const savePeripheralSetting = async () => {
		waitForResponse = true;
		const res: SettingRequest = await peripheralSettings.save();
		waitForResponse = false;
		selectedPeripheral = null;
		if (res?.isok) dispatcher('save');
	};

	const goToDevice = (device: Device) => {
		if (device?.id) {
			replace(`/home/room/${device.room_id}/devices`);

			setSidePanelState({
				type: 'device',
				id: device.id,
				open: true,
			});
		}
	};

	const buildPeripheralLabels = (item: Peripheral) => {
		if (item.peripheralType === 'serial') {
			return buildSerialPeripheralLabels(item);
		}
		return [{ icon: item.icon, value: item.value }];
	};

	const openPeripheralSettings = (item: Peripheral) => {
		selectedPeripheral = item;
		editPeripheralSettingsModal = true;
	};

	let peripherals: Peripheral[];
	let selectedPeripheral: Peripheral;
	let editPeripheralSettingsModal = false;
	let removePeripheralModal = false;
	let waitForResponse = false;
	let showModal = false;
	let settingsErrors = false;
	let peripheralSettings: PeripheralSettings;

	$: disableDeleteButton = isPill(device.type);

	$: if ($statusStore) {
		peripherals = proRS485AddonSupport(device)
			? getSerialAddonStatus(device, [], true)
			: [...getAddonStatus(device), ...getSerialAddonStatus(device)];
	}
</script>

<div class="peripherals-container">
	{#if peripherals.length}
		{#each peripherals as item, i}
			{@const peripheralDevice = getPeripheralDevice(device.id, item)}
			<AdvancedCard
				size="m"
				class={`mb-s${peripheralDevice ? ' cursor-pointer' : ''}`}
				avatar={{
					image: peripheralDevice?.image
						? load_image_from(peripheralDevice.image, device?.server)
						: '',
					icon: item.icon,
				}}
				title={item.sensor.name || item.sensor.label}
				hideSlotStatusBar={!peripheralDevice}
				on:click={() => {
					goToDevice(peripheralDevice);
				}}
			>
				<LabelList size="m" class="mt-xs" labelList={buildPeripheralLabels(item)} slot="main" />
				<CardStatusBar
					slot="statusBar"
					statsList={[
						{
							icon: 'far fa-puzzle-piece',
							iconColor: 'warning',
							text: $format('label_peripheral_device', {
								default: 'Peripheral Device',
							}),
						},
					]}
				/>
				<ActionsContainer orientation="vertical" type="simple" slot="right">
					<IconButton
						aria-label={$format('button_edit_peripheral_settings', {
							default: 'Edit Peripheral Settings',
						})}
						variant="primary"
						icon="fas fa-gear"
						slot="first"
						on:click={(e) => {
							e.stopPropagation();
							openPeripheralSettings(item);
						}}
					/>
					{#if !proRS485AddonSupport(device)}
						<IconButton
							aria-label={$format('button_delete_peripheral', {
								default: 'Delete Peripheral',
							})}
							slot="second"
							icon="fas fa-trash"
							disabled={disableDeleteButton}
							variant="danger"
							on:click={(e) => {
								e.stopPropagation();
								selectedPeripheral = item;
								removePeripheralModal = true;
							}}
						/>
					{/if}
				</ActionsContainer>
			</AdvancedCard>
		{/each}
	{:else}
		<Alert
			class="mb-s"
			heading={$format('alert_heading_no_peripherals_added', {
				default: 'No peripherals added',
			})}
			isHeadingBold
			message={$format('alert_message_first_add_peripheral', {
				default: 'If you want to use this feature, you first need to add a peripheral.',
			})}
		/>
	{/if}
	{#if !isPill(device.type) && !proRS485AddonSupport(device)}
		<Button class="mb-l" size="m" width="full" on:click={() => (showModal = true)}>
			{$format('button_add_peripheral', {
				default: 'Add peripheral',
			})}
		</Button>
	{/if}
</div>
{#if showModal}
	<AddPeripheralModal {device} bind:showModal />
{:else if editPeripheralSettingsModal && selectedPeripheral}
	<Modal
		showModal={editPeripheralSettingsModal}
		on:close={() => (editPeripheralSettingsModal = false)}
		title={$format('peripheral_settings', {
			default: 'Peripheral Settings',
		})}
		removeScrollPadding
	>
		<svelte:fragment slot="content">
			<Container padding="scrollPaddings">
				<PeripheralSettings
					{device}
					peripheral={selectedPeripheral}
					bind:this={peripheralSettings}
					bind:settingsErrors
				/>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (editPeripheralSettingsModal = false)}
				>{$format('button_cancel', { default: 'Cancel' })}</Button
			>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={settingsErrors || waitForResponse}
				loading={waitForResponse}
				on:click={savePeripheralSetting}>{$format('button_save', { default: 'Save' })}</Button
			>
		</div>
	</Modal>
{:else if removePeripheralModal && selectedPeripheral}
	<Modal
		showModal={removePeripheralModal}
		on:close={() => (removePeripheralModal = false)}
		title={$format('button_delete', { default: 'Delete' })}
		><svelte:fragment slot="content">
			<Container padding="0">
				<Text class="mb-s">
					{$format('on_delete_msg', {
						values: {
							this_type: selectedPeripheral.sensor.name,
						},
						default: 'Are you sure you want to delete {this_type}?',
					})}
				</Text>
				<Text>
					{$format('remove_peripheral_reboot_info', {
						default: 'After removing a peripheral device will reboot.',
					})}
				</Text>
				{@const peripheralDevice = getPeripheralDevice(real_id(device.id), selectedPeripheral)}
				{#if peripheralDevice}
					<Text class="mt-s mb-m">
						{$format('on_delete_msg_peripheral_device', {
							default: 'Your peripheral device will also be deleted.',
						})}
					</Text>
					<DeviceInList device={peripheralDevice} />
				{/if}
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (removePeripheralModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
				loading={waitForResponse}
				on:click={removePeripheral}
			>
				{$format('button_delete', { default: 'Delete' })}
			</Button>
		</div>
	</Modal>
{/if}
